import React, { useEffect, ReactElement, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Card, Button, Stack, Tabs, Tab } from 'react-bootstrap';
import { map, isUndefined, isNull, camelCase } from 'lodash';
import { useTranslation } from 'react-i18next';
import pluralize from 'pluralize';

import { BackButton, ClaimBadge, Loading, LoadingError, NoData } from '@components/Base';
import { PaymentMethods } from '@models/PaymentInfo';
import { ClaimableTypes } from '@models/Claim';
import { amountToUsd } from '@utils/currency';
import { formatDateTime, formatDate } from '@utils/datetime';

import { selectHealthClaim } from './selector';
import { healthClaimFetch } from './action';
import { useEditable } from '../hook';
import { ClaimEditModal } from '../../ClaimEditModal';

import { DocumentsTabContent } from './documents-tab-content';

interface RowItemProps {
  label: string;
  value?: string | number | ReactElement | null;
  list?: string[];
  renderItem?: (item: string) => void;
}

const RowItem = ({ label, value, list, renderItem }: RowItemProps) => {
  return (
    <Row className="mb-2">
      <Col xs={4} sm={4} md={4}>
        {label}
      </Col>
      <Col xs="auto">:</Col>
      <Col>
        <>
          {value && value}
          {list && renderItem && map(list, renderItem)}
        </>
      </Col>
    </Row>
  );
};

const HealthClaimDetails = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { claimableType, claimableId } = useParams();
  const { healthClaim } = useSelector(selectHealthClaim);
  const isLoaded = !isUndefined(healthClaim);
  const isError = isNull(healthClaim);
  const hasHealthClaim = isLoaded && !isNull(healthClaim);
  const shouldEdit = useEditable(healthClaim);
  const isPaymentByBankTransfer = healthClaim?.paymentInfo?.paymentMethod === PaymentMethods.BankTransfer;
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleReload = () => {
    if (claimableId) {
      dispatch(healthClaimFetch({ id: Number(claimableId) }));
    }
  };

  useEffect(() => {
    if (claimableId) {
      dispatch(healthClaimFetch({ id: Number(claimableId) }));
    }
  }, [claimableId]);

  return (
    <>
      <BackButton className="mb-4" />
      {!isLoaded && <Loading />}
      {isError && <LoadingError />}
      {isLoaded && !hasHealthClaim && <NoData />}

      {healthClaim && (
        <Card>
          <Card.Header>
            <Stack direction="horizontal" gap={2} className="align-items-center">
              <h5 className="mb-0 me-auto">{t('common:details')}</h5>
              {shouldEdit && (
                <Button size="sm" onClick={() => setIsModalOpen(true)}>
                  {t('common:edit')}
                </Button>
              )}
            </Stack>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6} className="mb-3">
                <RowItem label={t('claimDetails.billingType')} value={healthClaim.billingTypeCode} />
              </Col>
              <Col md={6} className="mb-3">
                <RowItem label={t('claimDetails.status')} value={<ClaimBadge status={healthClaim.status} />} />
              </Col>
              <Col md={6} className="mb-3">
                <RowItem label={t('claimDetails.claimNumber')} value={healthClaim.claimNumber} />
              </Col>
              <Col md={6} className="mb-3">
                <RowItem label={t('claimDetails.registerDate')} value={formatDateTime(healthClaim.createdAt)} />
              </Col>
              <Col md={6} className="mb-3">
                <RowItem
                  label={t('healthClaimDetails.dateOfAdmission')}
                  value={formatDateTime(healthClaim.accidentHappenedAt)}
                />
              </Col>
              <Col md={6} className="mb-3">
                <RowItem
                  label={t('claimDetails.totalClaimedAmount')}
                  value={amountToUsd(healthClaim.totalClaimedAmount)}
                />
              </Col>
              <Col md={6} className="mb-3">
                <RowItem label={t('common:policyNumber')} value={healthClaim.policyNumber} />
              </Col>
              <Col md={6} className="mb-3">
                <RowItem label={t('healthClaimDetails.companyName')} value={healthClaim.companyName} />
              </Col>
              <Col md={6} className="mb-3">
                <RowItem label={t('common:planType')} value={healthClaim.benefitTemplateName} />
              </Col>
              <Col md={6} className="mb-3">
                <RowItem label={t('healthClaimDetails.memberName')} value={healthClaim.insurancePolicyUserName} />
              </Col>
              <Col md={6} className="mb-3">
                <RowItem
                  label={t('common:periodOfInsurance')}
                  value={`${formatDate(healthClaim.insurancePolicyUserEffectiveDate)} - ${formatDate(
                    healthClaim.insurancePolicyUserExpirationDate,
                  )}`}
                />
              </Col>

              <Col md={6} className="mb-3">
                <RowItem
                  label={t('paymentInfo.paymentMethod')}
                  value={t(`paymentInfo.${camelCase(healthClaim.paymentInfo?.paymentMethod)}`)}
                />
              </Col>
              <Col md={6} className="mb-3">
                <RowItem label={t('paymentInfo.beneficiaryName')} value={healthClaim.paymentInfo?.beneficiaryName} />
              </Col>
              {isPaymentByBankTransfer && (
                <>
                  <Col md={6} className="mb-3">
                    <RowItem label={t('paymentInfo.bankName')} value={healthClaim.paymentInfo?.bankName} />
                  </Col>
                  <Col md={6} className="mb-3">
                    <RowItem label={t('paymentInfo.bankAccount')} value={healthClaim.paymentInfo?.bankAccount} />
                  </Col>
                </>
              )}
            </Row>
          </Card.Body>

          <Card.Body>
            <Tabs defaultActiveKey="documents" className="mb-3">
              <Tab eventKey="documents" title={pluralize(t('claimDetails.document.title'))}>
                <DocumentsTabContent />
              </Tab>
            </Tabs>
          </Card.Body>
        </Card>
      )}

      {healthClaim && (
        <ClaimEditModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          claimNumber={healthClaim.claimNumber}
          claimableType={ClaimableTypes.Health}
          claimableId={healthClaim.id}
          currentStatus={healthClaim.status}
          currentTotalClaimedAmount={healthClaim.totalClaimedAmount}
          onSuccess={handleReload}
        />
      )}
    </>
  );
};

export { HealthClaimDetails };
